<template>
	<modal size="medium" @close="handleClose">
		<template slot="header">
			<h3>Commission Settings</h3>
		</template>

		<template slot="body">
			<form @submit.prevent="submit">
				<div class="form-group">
					<label>Type</label>
					<select class="form-control" v-model="form.type">
						<option value="percentage">Percentage</option>
						<option value="fixed">Fixed</option>
					</select>
					<div v-if="errors.type" class="text-danger small">{{ errors.type }}</div>
				</div>

				<div class="form-group">
					<label>Value</label>
					<input
						type="number"
						class="input form-control"
						min="0"
						max="100"
						step="0.01"
						v-model.number="form.value"
					/>
					<div v-if="errors.value" class="text-danger small">{{ errors.value }}</div>
				</div>

				<div class="form-group" v-if="form.type === 'percentage'">
					<label>Minimum Commission (optional)</label>
					<input
						type="number"
						class="input form-control"
						min="0"
						step="0.01"
						v-model.number="form.minimum_commission"
					/>
					<div v-if="errors.minimum_commission" class="text-danger small">{{ errors.minimum_commission }}</div>
				</div>

				<div class="form-group">
					<label>Selling Price</label>
					<select class="form-control" v-model="form.impact">
						<option value="affect_selling_price">Added to selling price</option>
						<option value="not_affect_selling_price">Does not affect selling price</option>
					</select>
					<div v-if="errors.impact" class="text-danger small">{{ errors.impact }}</div>
				</div>
			</form>
		</template>

		<template slot="footer">
			<button type="button" class="btn btn-light" @click="handleClose" :disabled="isSaving">Cancel</button>
			<button type="button" class="btn btn-primary" :class="{ 'is-loading': isSaving }" @click="submit">Save</button>
		</template>
	</modal>
</template>

<script>
import Modal from '@/components/Modal.vue';
import adminApi from '@/api/admin';
import { duplicateVar, getAxiosErrorMessage } from '@/lib/helper';
import { DEFAULT_COMMISSION_CONFIG } from '@/lib/statics';

const COMMISSION_TYPES = ['percentage', 'fixed'];
const IMPACT_MODES = ['affect_selling_price', 'not_affect_selling_price'];

export default {
	name: 'CommissionSettingsModal',
	components: { Modal },
	props: {
		product: {
			type: Object,
			required: true,
		},
	},
	data() {
		return {
			form: duplicateVar(DEFAULT_COMMISSION_CONFIG),
			errors: {},
			isSaving: false,
		};
	},
	watch: {
		product: {
			immediate: true,
			handler() {
				this.resetForm();
			},
		},
		'form.type': function (next) {
			if (next !== 'percentage') {
				this.form.minimum_commission = null;
			}
		},
	},
	methods: {
		resetForm() {
			this.form = {
				...duplicateVar(DEFAULT_COMMISSION_CONFIG),
				...(this.product?.commission_config || {}),
			};
			if (this.form.type !== 'percentage') {
				this.form.minimum_commission = null;
			}
			this.errors = {};
		},
		validate() {
			this.errors = {};
			if (!COMMISSION_TYPES.includes(this.form.type)) {
				this.errors.type = 'Type is required';
			}
			if (this.form.value === '' || this.form.value === null || Number(this.form.value) < 0) {
				this.errors.value = 'Value must be a number greater than or equal to 0';
			}
			if (this.form.type === 'percentage' && this.form.minimum_commission !== null && Number(this.form.minimum_commission) < 0) {
				this.errors.minimum_commission = 'Minimum commission must be 0 or greater';
			}
			if (!IMPACT_MODES.includes(this.form.impact)) {
				this.errors.impact = 'Selling price is required';
			}
			return Object.keys(this.errors).length === 0;
		},
		handleClose() {
			this.$emit('close');
		},
		submit() {
			if (!this.validate()) {
				return;
			}
			this.isSaving = true;
			const productId = this.product._id;
			const minimum = this.form.type === 'percentage'
				&& this.form.minimum_commission !== null
				&& this.form.minimum_commission !== '' ? Number(this.form.minimum_commission) : null;
			const payload = {
				commission_config: {
					type: this.form.type,
					value: Number(this.form.value),
					impact: this.form.impact,
					minimum_commission: minimum,
				},
			};

			const callback = (response) => {
				this.isSaving = false;
				this.$notify({
					group: 'products',
					title: 'Commission Settings',
					text: response.message ?? 'Commission updated',
					type: 'success',
				});
				this.$emit('success', response.data);
			};
			const errorCallback = (e) => {
				this.isSaving = false;
				const msg = getAxiosErrorMessage(e);
				this.$notify({
					group: 'products',
					title: 'Commission Settings',
					text: msg || 'Failed to update commission',
					type: 'error',
				});
			};


			adminApi.updateProduct(productId, payload, callback, errorCallback);
		},
	},
};
</script>

<style scoped>
.text-danger {
	margin-top: 0.25rem;
}
</style>
